import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  closeOnEsc?: boolean;
  title?: ReactNode;
  children?: ReactNode;
}

export function Modal({
  isOpen,
  onClose,
  closeOnEsc = true,
  title,
  children,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Track the real visibility state for animations
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Intentional: keep mounted while the open/close animation plays out.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsVisible(true);
    } else {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    // Store current body style
    const savedBodyStyle = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      width: document.body.style.width,
      top: document.body.style.top,
    };

    // Lock body scroll
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${window.scrollY}px`;

    // Focus trap
    const focusableElements = dialogRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const firstFocusableElement = focusableElements?.[0];
    const lastFocusableElement =
      focusableElements?.[focusableElements.length - 1];

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEsc && isOpen) {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      } else if (e.key === 'Tab' && isOpen) {
        handleTabKey(e);
      }
    };

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          lastFocusableElement?.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          firstFocusableElement?.focus();
          e.preventDefault();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    firstFocusableElement?.focus();

    return () => {
      // Restore body scroll
      document.body.style.overflow = savedBodyStyle.overflow;
      document.body.style.position = savedBodyStyle.position;
      document.body.style.width = savedBodyStyle.width;
      document.body.style.top = savedBodyStyle.top;
      window.scrollTo(0, parseInt(savedBodyStyle.top || '0') * -1);

      document.removeEventListener('keydown', handleKeyDown);
    };
    // Runs only on open/close; including the unstable onClose/closeOnEsc props
    // would re-run focus management and scroll locking on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if ((!isVisible && !isOpen) || !children) return null;

  return createPortal(
    <div
      className={`modal ${isOpen ? 'fade-in' : 'fade-out'}`}
      onClick={onClose}
    >
      <div
        className="modal__content"
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <>
            <div className="modal__header">
              <h3 className="modal__title">{title}</h3>
            </div>
          </>
        )}
        <div className="modal__content-inner">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
